import * as React from 'react';

import { cn } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import * as conversationState from '../../_state/chatConversation.state';
import type { ChatMessageRecord } from '../../types';

import { ErrorMessageDisplay } from './ErrorMessageDisplay';
import { MessageItem } from './MessageItem';
import { TypingIndicator } from './TypingIndicator';

interface MessageListProps {
  messages: ChatMessageRecord[];
}

//
// ⚛️ ⚛️ ⚛️ MESSAGE LIST ⚛️ ⚛️ ⚛️
//
export const MessageList = ({ messages }: MessageListProps) => {
  // Refs
  const containerRef = React.useRef<HTMLDivElement>(null);
  const userMessageRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const shouldAutoScroll = React.useRef(true);
  const prevMessageCountRef = React.useRef(0);

  // States
  const [scrollState, setScrollState] = React.useState<{
    mode: 'scrolling-to-user' | 'locked' | 'idle';
    targetMessageId: string | null;
  }>({ mode: 'idle', targetMessageId: null });

  // Atoms
  const isAgentWorking = useAtomValue(conversationState.$isAgentWorking);
  const streamingError = useAtomValue(conversationState.$streamingError);
  const setStreamingError = useSetAtom(conversationState.$streamingError);

  // Memoized values
  const hasStreamingAssistantContent = React.useMemo(() => {
    if (messages.length === 0) return false;
    const last = messages[messages.length - 1];
    if (last.role !== 'Assistant') return false;
    return last.parts.some(
      (p) => p.data.type === 'Text' && p.data.state === 'Streaming',
    );
  }, [messages]);

  const isTyping = React.useMemo(() => {
    if (streamingError) return false;
    if (isAgentWorking && !hasStreamingAssistantContent) return true;

    if (messages.length === 0 || streamingError) return false;

    const lastMessage = messages[messages.length - 1];

    if (lastMessage.role === 'User') return true;

    const hasTextContent = lastMessage.parts.some((p) => {
      if (p.data.type !== 'Text') return false;
      return p.data.text.length > 0;
    });

    if (!hasTextContent && !isAgentWorking) return false;

    return !hasTextContent;
  }, [streamingError, isAgentWorking, hasStreamingAssistantContent, messages]);

  // Callbacks
  const setUserMessageRef = React.useCallback(
    (messageId: string, element: HTMLDivElement | null) => {
      if (element) {
        userMessageRefs.current.set(messageId, element);
      } else {
        userMessageRefs.current.delete(messageId);
      }
    },
    [],
  );

  // Effects
  /** Scroll to bottom when messages first load */
  React.useEffect(() => {
    const wasEmpty = prevMessageCountRef.current === 0;
    prevMessageCountRef.current = messages.length;

    if (!wasEmpty || messages.length === 0) return;
    if (isAgentWorking) return;

    const container = containerRef.current;
    if (!container) return;

    requestAnimationFrame(() => {
      container.scrollTo?.({ top: container.scrollHeight, behavior: 'smooth' });
    });
  }, [messages, isAgentWorking]);

  /** Detect new user messages */
  React.useEffect(() => {
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];

    if (
      lastMessage.role === 'User' &&
      lastMessage.id !== scrollState.targetMessageId
    ) {
      shouldAutoScroll.current = true;
      setScrollState({
        mode: 'scrolling-to-user',
        targetMessageId: lastMessage.id,
      });
    }
  }, [messages, scrollState.targetMessageId]);

  /** Handle scroll-to-user behavior */
  React.useEffect(() => {
    if (scrollState.mode !== 'scrolling-to-user') return;
    if (!scrollState.targetMessageId) return;

    const container = containerRef.current;
    const messageElement = userMessageRefs.current.get(
      scrollState.targetMessageId,
    );

    if (!container || !messageElement) return;

    const containerRect = container.getBoundingClientRect();
    const messageRect = messageElement.getBoundingClientRect();

    const isAtTop = Math.abs(messageRect.top - containerRect.top) < 20;

    if (isAtTop) {
      setScrollState((prev) => ({ ...prev, mode: 'locked' }));
    } else {
      const scrollTop = messageElement.offsetTop - container.offsetTop;
      const maxScroll = container.scrollHeight - container.clientHeight;

      if (scrollTop > maxScroll) {
        container.scrollTo?.({
          top: maxScroll,
          behavior: 'smooth',
        });
        setScrollState((prev) => ({ ...prev, mode: 'locked' }));
      } else {
        container.scrollTo?.({
          top: scrollTop,
          behavior: 'smooth',
        });
      }
    }
  }, [scrollState, messages, isAgentWorking, streamingError]);

  /** Reset to idle and scroll to bottom when agent finishes */
  React.useEffect(() => {
    if (!isAgentWorking && scrollState.mode === 'locked') {
      setScrollState({ mode: 'idle', targetMessageId: null });
      const container = containerRef.current;
      if (container && shouldAutoScroll.current) {
        requestAnimationFrame(() => {
          container.scrollTo?.({
            top: container.scrollHeight,
            behavior: 'smooth',
          });
        });
      }
    }
  }, [isAgentWorking, scrollState.mode]);

  /** Detect user scroll to disable auto-scroll when scrolled up */
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const distanceFromBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight;
      shouldAutoScroll.current = distanceFromBottom < 100;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  /** Auto-scroll to bottom while agent is streaming */
  React.useEffect(() => {
    if (!isAgentWorking || !shouldAutoScroll.current) return;

    const container = containerRef.current;
    if (!container) return;

    requestAnimationFrame(() => {
      container.scrollTo?.({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    });
  }, [messages, isAgentWorking]);

  return (
    <div ref={containerRef} className="@MessageList flex-1 overflow-auto p-4">
      <div className="flex flex-col">
        {messages.map((message, index) => {
          const prev = messages[index - 1];
          const next = messages[index + 1];
          const sameMinute =
            next !== undefined &&
            message.createdAt.hour === next.createdAt.hour &&
            message.createdAt.minute === next.createdAt.minute &&
            message.createdAt.day === next.createdAt.day;
          const roleChanged = prev !== undefined && prev.role !== message.role;
          const spacing = cn(
            index > 0 && roleChanged && 'mt-4',
            index > 0 && !roleChanged && 'mt-1',
          );
          return (
            <div key={message.id} className={spacing}>
              <MessageItem
                message={message}
                showTimestamp={!sameMinute}
                setRef={message.role === 'User' ? setUserMessageRef : undefined}
              />
            </div>
          );
        })}

        {isTyping && <TypingIndicator />}

        {streamingError && (
          <ErrorMessageDisplay
            error={streamingError.error}
            errorCode={streamingError.errorCode}
            onDismiss={() => setStreamingError(null)}
          />
        )}

        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};
